/**
 * Deterministic integer hashing + PRNG.
 *
 * Only integer ops (Math.imul, shifts, xor) and exact float scaling are used, so results are
 * bit-identical on every JS engine. Never use Math.random() anywhere in world generation or sim.
 */

/** 32-bit avalanche (lowbias32 by Chris Wellons). */
export function mix32(x: number): number {
  x = x >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d);
  x ^= x >>> 15;
  x = Math.imul(x, 0x846ca68b);
  x ^= x >>> 16;
  return x >>> 0;
}

/** Hash up to four integers into one 32-bit value. */
export function hash32(a: number, b = 0, c = 0, d = 0): number {
  let h = mix32(a ^ 0x9e3779b9);
  h = mix32(h ^ Math.imul(b | 0, 0x85ebca6b));
  h = mix32(h ^ Math.imul(c | 0, 0xc2b2ae35));
  h = mix32(h ^ Math.imul(d | 0, 0x27d4eb2f));
  return h;
}

/** Uniform float in [0, 1) from a 32-bit hash. Exact (division by a power of two). */
export function hashFloat(a: number, b = 0, c = 0, d = 0): number {
  return hash32(a, b, c, d) / 4294967296;
}

/** Hash a string (e.g. a seed code) into 32 bits. FNV-1a + final avalanche. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return mix32(h);
}

/** Small fast PRNG (sfc32). Stream = (seed, purpose, index) so callers never share state. */
export class Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  constructor(seed: number, stream = 0, index = 0) {
    this.a = hash32(seed, stream, index, 1);
    this.b = hash32(seed, stream, index, 2);
    this.c = hash32(seed, stream, index, 3);
    this.d = 1;
    for (let i = 0; i < 12; i++) this.u32();
  }

  u32(): number {
    const t = (((this.a + this.b) | 0) + this.d) | 0;
    this.d = (this.d + 1) | 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) | 0;
    this.c = (this.c << 21) | (this.c >>> 11);
    this.c = (this.c + t) | 0;
    return t >>> 0;
  }

  /** [0, 1) */
  float(): number {
    return this.u32() / 4294967296;
  }

  range(min: number, max: number): number {
    return min + (max - min) * this.float();
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return min + Math.floor(this.float() * (max - min + 1));
  }

  chance(p: number): boolean {
    return this.float() < p;
  }

  sign(): number {
    return this.float() < 0.5 ? -1 : 1;
  }

  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.float() * arr.length)]!;
  }

  weighted<T extends { weight: number }>(arr: readonly T[], weightOf: (t: T) => number = (t) => t.weight): T {
    let total = 0;
    for (const t of arr) total += weightOf(t);
    let r = this.float() * total;
    for (const t of arr) {
      r -= weightOf(t);
      if (r < 0) return t;
    }
    return arr[arr.length - 1]!;
  }
}

/** Named streams so unrelated systems never perturb each other's sequences. */
export const STREAM = {
  profile: 1,
  curve: 2,
  hazards: 3,
  decor: 4,
  gaps: 5,
  grass: 6,
  props: 7,
  skyIslands: 8,
} as const;
